import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { SiteLogo } from '../ui/SiteLogo';
import { AuthCarousel } from '../auth/AuthCarousel';
import { AuthFormSection } from '../auth/AuthFormSection';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { HeaderMobileMenu, mobileMenuItemClass } from './HeaderMobileMenu';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { PublicFooter } from './PublicFooter';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0 }
};

export const AuthLayout = ({ children }) => {
  const { t } = useI18n();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  const navItems = [
    { label: t('navHome'), to: '/' },
    { label: t('navPricing'), to: '/pricing' },
    { label: t('navAbout'), to: '/about' }
  ];

  return (
    <main className="relative isolate min-h-screen overflow-hidden bg-app px-4 text-text sm:px-6 lg:px-8">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-1/2 top-[-18rem] h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-accent-soft blur-3xl" />
        <div className="absolute bottom-[-20rem] right-[-8rem] h-[34rem] w-[34rem] rounded-full bg-teal-400/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(15,61,145,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(15,61,145,0.045)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_80%)] dark:bg-[linear-gradient(rgba(104,213,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(104,213,255,0.04)_1px,transparent_1px)]" />
      </div>

      <div className="relative mx-auto flex min-h-full max-w-6xl flex-col">
        <header className="flex items-center justify-between gap-3 py-5 sm:py-6">
          <Link to="/" className="flex min-w-0 items-center gap-3 transition hover:opacity-90">
            <SiteLogo />
            <div className="text-start">
              <p className="text-sm font-bold tracking-tight">{t('landingBrand')}</p>
              <p className="text-xs text-muted">{t('landingTopTag')}</p>
            </div>
          </Link>

          <nav
            className="hidden items-center justify-center rounded-full border border-border bg-panel/80 p-1 shadow-sm backdrop-blur-xl lg:flex"
            aria-label={t('navigation')}
          >
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-full px-4 py-2 text-sm font-semibold text-muted transition hover:bg-accent-soft hover:text-text"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center justify-end gap-2">
            <HeaderMobileMenu
              open={mobileNavOpen}
              onOpen={openMobileNav}
              onClose={closeMobileNav}
              brandTitle={t('landingBrand')}
              brandSubtitle={t('landingTopTag')}
            >
              {navItems.map((item) => (
                <Link key={item.to} to={item.to} className={mobileMenuItemClass()}>
                  {item.label}
                </Link>
              ))}
            </HeaderMobileMenu>

            <LanguageSwitcher />
            <Link
              to={isAuthenticated ? '/home' : '/login'}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-accent px-3 text-sm font-bold text-white shadow-sm transition hover:opacity-95 dark:text-slate-950 sm:px-4"
              aria-label={t('account')}
            >
              <UserRound size={16} />
              <span className="hidden sm:inline">{t('account')}</span>
            </Link>
          </div>
        </header>

        <motion.div
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.06 }}
          className="flex flex-1 flex-col items-center gap-10 py-4 pb-12 lg:grid lg:grid-cols-2 lg:items-center lg:gap-14 lg:py-6"
        >
          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="order-2 w-full max-w-xl lg:order-none lg:max-w-none"
          >
            <AuthCarousel />
          </motion.div>

          <motion.section
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 flex w-full justify-center lg:order-none lg:justify-end lg:py-4"
          >
            <AuthFormSection>{children}</AuthFormSection>
          </motion.section>
        </motion.div>

        <PublicFooter />
      </div>
    </main>
  );
};
